import dayjs from 'dayjs';
import {
	getClaimsBilling,
	getMembersAggregationBilling,
	getMembersBilling,
	resetBillingState,
	resetClaimsBilling,
	setSubTabSelected,
	setTabSelected,
} from 'feature/billing/billingSlice';
import {
	handleChange,
	resetForm,
	resetMemberFilters,
	setSearch,
	setSortingValues,
} from 'feature/form/formSlice';
import { resetTableSelection } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useBillingSubTab } from 'hooks/billingSubTab.hook';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { EBillingSubTab } from 'types/Billing';
import { DEBOUNCE_DELAY } from 'utils/constants';

const useBillingFilter = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { pages, tabSelected, masterSort } = useSelector(
		(store: RootState) => store.billing,
	);

	const { isMembers, isMembersAggregation, subTabSelected, view } =
		useBillingSubTab();
	const page = pages[view];

	const {
		inputBillingId,
		inputBillingTypologies,
		inputBillingStatus,
		inputBillingCarriers,
		inputBillingMarkets,
		inputClients,
		inputGroups,
		inputExternalClaimId,
		inputCarrierClaimId,
		inputClaimStartDate,
		inputClaimEndDate,
		inputPaidStartDate,
		inputPaidEndDate,
		inputClaimStatus,
		inputBillingNetworkStatus,
		inputClaimBillingStartDate,
		inputClaimBillingEndDate,
		inputClaimSettlementStartDate,
		inputClaimSettlementEndDate,
		sortingDirections,
		sortingFields,
		inputMemberBillingId,
		inputMemberBillingStatus,
		inputMemberBillingMonthStart,
		inputMemberBillingMonthEnd,
		inputMemberClient,
		inputMemberGroups,
		inputMemberPlanCodes,
		inputMemberDivisionCode,
		inputMemberLastName,
		inputMemberFirstName,
		inputMemberRelationshipTypes,
		inputMemberMemberId,
		inputMemberEmMemberId,
		inputMemberSubscriptionStartDate,
		inputMemberSubscriptionEndDate,
		inputAggregationBillingMonthStart,
		inputAggregationBillingMonthEnd,
		inputAggregationClient,
		inputAggregationGroups,
		inputAggregationPlanCodes,
	} = useSelector((store: RootState) => store.form);

	//set the initial value of sorting as soon as the page open
	useEffect(() => {
		dispatch(setSearch(true));

		//since members is a new page this could trigger for that page when we dont want it to.
		if (!isMembers) {
			dispatch(
				setSortingValues({
					name: 'externalClaimId,claimDate',
					value: 'DESC,DESC',
				}),
			);
		}
	}, [dispatch, isMembers]);

	//reset the redux once we close the page
	useEffect(() => {
		return () => {
			dispatch(resetForm());
			dispatch(resetTableSelection());
			dispatch(resetBillingState());
		};
	}, [dispatch]);

	//changing pages reset the selected rows
	useEffect(() => {
		return () => {
			dispatch(resetTableSelection());
		};
	}, [dispatch, isMembers, isMembersAggregation]);

	// Tab/subTab change: set up sorting defaults and initial carriers value.
	// Back from the detail, the master gets back the sort it had.
	useEffect(() => {
		if (isMembers) {
			dispatch(setSortingValues({ name: 'billingId', value: 'DESC' }));
		} else if (isMembersAggregation) {
			dispatch(
				setSortingValues(
					masterSort ?? { name: 'billingMonth,id', value: 'DESC,DESC' },
				),
			);
		} else {
			dispatch(handleChange({ name: 'inputBillingCarriers', value: ['All'] }));
		}
	}, [
		dispatch,
		tabSelected,
		subTabSelected,
		isMembers,
		isMembersAggregation,
		masterSort,
	]);

	const [changedBillingFilters, setChangedBillingFilters] = useState(0);
	const [changedAggregationFilters, setChangedAggregationFilters] = useState(0);
	const [changedMembersFilters, setChangedMembersFilters] = useState(0);

	useEffect(() => {
		setChangedBillingFilters(prev => prev + 1);
	}, [
		inputBillingId,
		inputBillingTypologies,
		inputBillingStatus,
		inputBillingCarriers,
		inputClients,
		inputGroups,
		inputExternalClaimId,
		inputCarrierClaimId,
		inputClaimStartDate,
		inputClaimEndDate,
		inputPaidStartDate,
		inputPaidEndDate,
		inputClaimStatus,
		inputBillingNetworkStatus,
		inputClaimBillingStartDate,
		inputClaimBillingEndDate,
		inputClaimSettlementStartDate,
		inputClaimSettlementEndDate,
	]);

	useEffect(() => {
		setChangedAggregationFilters(prev => prev + 1);
	}, [
		inputAggregationBillingMonthStart,
		inputAggregationBillingMonthEnd,
		inputAggregationClient,
		inputAggregationGroups,
		inputAggregationPlanCodes,
	]);

	useEffect(() => {
		setChangedMembersFilters(prev => prev + 1);
	}, [
		inputMemberBillingId,
		inputMemberBillingStatus,
		inputMemberBillingMonthStart,
		inputMemberBillingMonthEnd,
		inputMemberClient,
		inputMemberGroups,
		inputMemberPlanCodes,
		inputMemberDivisionCode,
		inputMemberLastName,
		inputMemberFirstName,
		inputMemberRelationshipTypes,
		inputMemberMemberId,
		inputMemberEmMemberId,
		inputMemberSubscriptionStartDate,
		inputMemberSubscriptionEndDate,
	]);

	// Debounced fetch triggered by filter changes. Resets to page 1.
	useEffect(() => {
		const handler = setTimeout(() => {
			if (isMembers) {
				dispatch(getMembersBilling(''));
			} else if (isMembersAggregation) {
				dispatch(getMembersAggregationBilling(''));
			} else {
				dispatch(getClaimsBilling(''));
			}
		}, DEBOUNCE_DELAY);

		return () => clearTimeout(handler);
	}, [
		dispatch,
		changedBillingFilters,
		changedMembersFilters,
		changedAggregationFilters,
		sortingDirections,
		sortingFields,
		inputBillingMarkets,
		isMembers,
		isMembersAggregation,
		page,
	]);
};

const useMembersBillingUrlSync = () => {
	const dispatch = useDispatch<AppDispatch>();
	const location = useLocation();

	useEffect(() => {
		dispatch(resetClaimsBilling());
	}, [dispatch]);

	useEffect(() => {
		// Fix the Redux tab state for hard refreshes on /view-billing/members
		// and restore any filter values that were encoded in the URL.
		dispatch(setTabSelected('admin'));
		dispatch(setSubTabSelected(EBillingSubTab.MEMBERS));

		const params = new URLSearchParams(location.search);

		const memberBillingId = params.get('memberBillingId');
		if (memberBillingId) {
			dispatch(
				handleChange({ name: 'inputMemberBillingId', value: memberBillingId }),
			);
		}

		const memberBillingStatus = params.get('memberBillingStatus');
		if (memberBillingStatus) {
			dispatch(
				handleChange({
					name: 'inputMemberBillingStatus',
					value: memberBillingStatus,
				}),
			);
		}

		const memberMonthStart = params.get('memberMonthStart');
		if (memberMonthStart) {
			dispatch(
				handleChange({
					name: 'inputMemberBillingMonthStart',
					value: dayjs(memberMonthStart, 'YYYY-MM'),
				}),
			);
		}

		const memberMonthEnd = params.get('memberMonthEnd');
		if (memberMonthEnd) {
			dispatch(
				handleChange({
					name: 'inputMemberBillingMonthEnd',
					value: dayjs(memberMonthEnd, 'YYYY-MM'),
				}),
			);
		}

		const memberClient = params.get('memberClient');
		if (memberClient) {
			dispatch(
				handleChange({ name: 'inputMemberClient', value: memberClient.split(',') }),
			);
		}

		const memberGroups = params.get('memberGroups');
		if (memberGroups) {
			dispatch(
				handleChange({ name: 'inputMemberGroups', value: memberGroups.split(',') }),
			);
		}

		const memberPlanCodes = params.get('memberPlanCodes');
		if (memberPlanCodes) {
			dispatch(
				handleChange({
					name: 'inputMemberPlanCodes',
					value: memberPlanCodes.split(','),
				}),
			);
		}

		const memberDivisionCode = params.get('memberDivisionCode');
		if (memberDivisionCode) {
			dispatch(
				handleChange({
					name: 'inputMemberDivisionCode',
					value: memberDivisionCode,
				}),
			);
		}

		const memberLastName = params.get('memberLastName');
		if (memberLastName) {
			dispatch(
				handleChange({ name: 'inputMemberLastName', value: memberLastName }),
			);
		}

		const memberFirstName = params.get('memberFirstName');
		if (memberFirstName) {
			dispatch(
				handleChange({ name: 'inputMemberFirstName', value: memberFirstName }),
			);
		}

		const memberRelationshipTypes = params.get('memberRelationshipTypes');
		if (memberRelationshipTypes) {
			dispatch(
				handleChange({
					name: 'inputMemberRelationshipTypes',
					value: memberRelationshipTypes.split(','),
				}),
			);
		}

		const memberMemberId = params.get('memberMemberId');
		if (memberMemberId) {
			dispatch(
				handleChange({ name: 'inputMemberMemberId', value: memberMemberId }),
			);
		}

		const memberEmMemberId = params.get('memberEmMemberId');
		if (memberEmMemberId) {
			dispatch(
				handleChange({ name: 'inputMemberEmMemberId', value: memberEmMemberId }),
			);
		}

		const memberSubscriptionStart = params.get('memberSubscriptionStart');
		if (memberSubscriptionStart) {
			dispatch(
				handleChange({
					name: 'inputMemberSubscriptionStartDate',
					value: dayjs(memberSubscriptionStart),
				}),
			);
		}

		const memberSubscriptionEnd = params.get('memberSubscriptionEnd');
		if (memberSubscriptionEnd) {
			dispatch(
				handleChange({
					name: 'inputMemberSubscriptionEndDate',
					value: dayjs(memberSubscriptionEnd),
				}),
			);
		}

		return () => {
			dispatch(resetMemberFilters());
		};
	}, [dispatch, location.search]);
};

export { useBillingFilter, useMembersBillingUrlSync };
